// frontend/src/components/Settings/NotificationEntityFieldsAdmin.jsx
// Admin page to manage which fields are filterable per notification entity type

import React, { useState, useEffect, useCallback } from 'react';
import { notificationsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import { FiPlus, FiEdit2, FiTrash2, FiDatabase } from 'react-icons/fi';
import '../Settings/Modal.css';

// ── Field type options ────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { value: 'text',    label: 'Texto livre' },
  { value: 'number',  label: 'Número' },
  { value: 'select',  label: 'Lista de opções (select)' },
  { value: 'lookup',  label: 'Tabela BD (lookup)' },
  { value: 'boolean', label: 'Sim / Não' },
];

// ── Empty form state ──────────────────────────────────────────────────────────
const emptyForm = () => ({
  entity_type:   '',
  field_name:    '',
  label:         '',
  field_type:    'text',
  field_options: '',
  lookup_table:  '',
  lookup_value:  '',
  lookup_label:  '',
  sort_order:    0,
  is_active:     true,
});

// ── Modal form (create or edit) ───────────────────────────────────────────────
const FieldModal = ({ initial, entityTypes, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial || emptyForm());
  const [hasChanges, setHasChanges] = useState(false);
  const setF = (k, v) => { setForm(f => ({ ...f, [k]: v })); setHasChanges(true); };

  const isLookup = form.field_type === 'lookup';
  const isSelect = form.field_type === 'select';
  const canSave  = form.entity_type && form.field_name && form.label && form.field_type;
  const isEdit   = !!initial?.field_id;

  // ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    console.log('[NotificationEntityFieldsAdmin.jsx] mounted');
    return () => console.log('[NotificationEntityFieldsAdmin.jsx] unmounted');
  }, []);


  const handleCancel = () => {
    if (hasChanges && !window.confirm(t('messages.confirm_close', 'Tem alterações não guardadas. Fechar?'))) return;
    onCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;
    const payload = {
      ...form,
      sort_order:    parseInt(form.sort_order) || 0,
      field_options: isSelect && form.field_options
        ? form.field_options.split(',').map(s => s.trim()).filter(Boolean)
        : null,
      lookup_table:  isLookup ? form.lookup_table || null : null,
      lookup_value:  isLookup ? form.lookup_value || null : null,
      lookup_label:  isLookup ? form.lookup_label || null : null,
    };
    onSave(payload);
  };

  const optionCount = isSelect && form.field_options
    ? form.field_options.split(',').map(s => s.trim()).filter(Boolean).length
    : 0;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 560 }}>

        <div className="modal-header">
          <h3>{isEdit ? 'Editar Campo' : 'Novo Campo de Filtro'}</h3>
          <button className="modal-close" type="button"
            title="Fechar (ESC)" onClick={handleCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {hasChanges && (
            <div className="form-warning">
              <span className="warning-icon">✏️</span>
              {t('messages.unsaved', 'Tem alterações não guardadas')}
            </div>
          )}

          {/* Row 1: entity_type + field_name */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Entity Type *</label>
              <input type="text" list="ef-entity-types" className="form-control"
                value={form.entity_type}
                onChange={e => setF('entity_type', e.target.value)}
                placeholder="ex: sample_order_header"
                autoFocus={!isEdit}
                disabled={isEdit}/>
              <datalist id="ef-entity-types">
                {entityTypes.map(et => <option key={et} value={et}/>)}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">Field Name (coluna DB) *</label>
              <input className="form-control"
                type="text"
                value={form.field_name}
                onChange={e => setF('field_name', e.target.value.toLowerCase().replace(/\s/g, '_'))}
                placeholder="ex: division_id"
                disabled={isEdit}/>
            </div>
          </div>

          {/* Row 2: label + field_type */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Label (display) *</label>
              <input className="form-control"
                type="text"
                value={form.label}
                onChange={e => setF('label', e.target.value)}
                placeholder="ex: Divisão"
                autoFocus={isEdit}/>
            </div>
            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <select className="form-control" value={form.field_type}
                onChange={e => setF('field_type', e.target.value)}>
                {FIELD_TYPES.map(ft => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Select options — only when type=select */}
          {isSelect && (
            <div className="form-group">
              <label className="form-label">
                Opções
                {optionCount > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600,
                    color: 'var(--primary)', background: '#e0f2f1',
                    padding: '1px 7px', borderRadius: 8 }}>
                    {optionCount} opções
                  </span>
                )}
              </label>
              <input className="form-control"
                type="text"
                value={form.field_options}
                onChange={e => setF('field_options', e.target.value)}
                placeholder="OPEN, CLOSED, CANCELLED  (separadas por vírgula)"/>
            </div>
          )}

          {/* Lookup fields — only when type=lookup */}
          {isLookup && (
            <>
              <div className="form-group">
                <label className="form-label">Lookup Table</label>
                <input type="text" className="form-control"
                  value={form.lookup_table}
                  onChange={e => setF('lookup_table', e.target.value)}
                  placeholder="schema.table_name  ex: plm.divisions"/>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Coluna ID (value)</label>
                  <input type="text" className="form-control"
                    value={form.lookup_value}
                    onChange={e => setF('lookup_value', e.target.value)}
                    placeholder="ex: division_id"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Coluna Display (label)</label>
                  <input type="text" className="form-control"
                    value={form.lookup_label}
                    onChange={e => setF('lookup_label', e.target.value)}
                    placeholder="ex: division_code"/>
                </div>
              </div>
            </>
          )}

          {/* Row: sort_order + is_active */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Ordem</label>
              <input type="number" className="form-control"
                value={form.sort_order}
                onChange={e => setF('sort_order', e.target.value)}
                min={0} step={10}/>
            </div>
            <div className="form-group" style={{ display: 'flex',
              flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div className="form-group-checkbox" style={{ marginBottom: 0 }}>
                <label>
                  <input type="checkbox" name="is_active"
                    checked={form.is_active}
                    onChange={e => setF('is_active', e.target.checked)}/>
                  <span>Ativo</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary"
              onClick={handleCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {isEdit ? 'Atualizar Campo' : 'Criar Campo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const NotificationEntityFieldsAdmin = () => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [fields,      setFields]      = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modalItem,   setModalItem]   = useState(null);  // null=closed, {}=new, {...field}=edit
  const [filterType,  setFilterType]  = useState('');

  const load = useCallback(async () => {
    try {
      const [fieldsRes, typesRes] = await Promise.all([
        notificationsAPI.getAllEntityFields(),
        notificationsAPI.getEntityFieldTypes(),
      ]);
      if (fieldsRes.data.success) setFields(fieldsRes.data.data);
      if (typesRes.data.success)  setEntityTypes(typesRes.data.data);
    } catch (err) {
      toast_error('Erro ao carregar campos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => {
    try {
      await notificationsAPI.createEntityField(payload);
      toast_success('Campo criado');
      setModalItem(null);
      await load();
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao criar campo');
    }
  };

  const handleUpdate = async (fieldId, payload) => {
    try {
      await notificationsAPI.updateEntityField(fieldId, payload);
      toast_success('Campo atualizado');
      setModalItem(null);
      await load();
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao atualizar campo');
    }
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`Eliminar campo "${field.label}" (${field.field_name})?`)) return;
    try {
      await notificationsAPI.deleteEntityField(field.field_id);
      toast_success('Campo eliminado');
      await load();
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao eliminar campo');
    }
  };

  const toggleActive = async (field) => {
    try {
      await notificationsAPI.updateEntityField(field.field_id, { is_active: !field.is_active });
      setFields(prev => prev.map(f =>
        f.field_id === field.field_id ? { ...f, is_active: !f.is_active } : f
      ));
    } catch (err) { toast_error('Erro ao actualizar'); }
  };

  // Build field_options display from JSONB
  const displayOptions = (f) => {
    if (f.field_type === 'select' && f.field_options) {
      const opts = Array.isArray(f.field_options) ? f.field_options : f.field_options;
      return Array.isArray(opts) ? opts.join(', ') : JSON.stringify(opts);
    }
    if (f.field_type === 'lookup') {
      return `${f.lookup_table} · ${f.lookup_value} → ${f.lookup_label}`;
    }
    return '—';
  };

  // Group by entity_type
  const allTypes = [...new Set(fields.map(f => f.entity_type))].sort();
  const visibleTypes = filterType ? [filterType] : allTypes;
  const grouped = visibleTypes.reduce((acc, et) => {
    acc[et] = fields.filter(f => f.entity_type === et);
    return acc;
  }, {});

  if (loading) return (
    <div className="settings-page"><div className="loading">A carregar…</div></div>
  );

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiDatabase size={20} color="var(--primary)"/>
            Campos de Filtro de Notificações
          </h2>
          <p className="page-subtitle">
            Define quais os campos disponíveis nos filtros de cada tipo de entidade
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalItem({})}>
          <FiPlus size={14}/> Novo Campo
        </button>
      </div>



      {/* Filter by entity type */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-600)' }}>
          Filtrar por entity type:
        </label>
        <select className="form-control" style={{ maxWidth: 240 }}
          value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Todos ({allTypes.length} tipos)</option>
          {allTypes.map(et => (
            <option key={et} value={et}>{et} ({fields.filter(f => f.entity_type === et).length})</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
          {fields.filter(f => f.is_active).length} ativos / {fields.length} total
        </span>
      </div>

      {/* Groups */}
      {Object.entries(grouped).map(([entityType, typeFields]) => (
        <div key={entityType} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 8, paddingBottom: 6,
            borderBottom: '2px solid var(--gray-200)' }}>
            {entityType}
            <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--gray-400)' }}>
              ({typeFields.length} campos)
            </span>
          </div>

          <div className="form-section" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)',
                  borderBottom: '1px solid var(--gray-200)' }}>
                  {['Ordem','Campo (DB)','Label','Tipo','Opções / Lookup','Ativo','Ações'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: 'var(--gray-500)',
                      whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {typeFields
                  .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label))
                  .map(field => (
                  <React.Fragment key={field.field_id}>
                    <tr style={{ borderBottom: '1px solid var(--gray-100)',
                      opacity: field.is_active ? 1 : 0.5 }}>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-400)',
                        fontSize: 12 }}>{field.sort_order}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <code style={{ background: 'var(--gray-100)', padding: '1px 6px',
                          borderRadius: 4, fontSize: 12 }}>
                          {field.field_name}
                        </code>
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                        {field.label}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11,
                          fontWeight: 600,
                          background: field.field_type === 'lookup' ? '#dbeafe'
                            : field.field_type === 'select' ? '#ede9fe'
                            : '#f3f4f6',
                          color: field.field_type === 'lookup' ? '#1d4ed8'
                            : field.field_type === 'select' ? '#6d28d9'
                            : '#374151' }}>
                          {field.field_type}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 12,
                        color: 'var(--gray-500)', maxWidth: 280,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' }}>
                        {displayOptions(field)}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <input type="checkbox" checked={field.is_active}
                          onChange={() => toggleActive(field)}
                          style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}/>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-action btn-edit"
                            title="Editar"
                            onClick={() => { setModalItem({
                              ...field,
                              field_options: Array.isArray(field.field_options)
                                ? field.field_options.join(', ')
                                : (field.field_options ? Object.values(field.field_options).join(', ') : ''),
                            }); }}>
                            <FiEdit2 size={16}/>
                          </button>
                          <button className="btn-action btn-delete"
                            title="Eliminar"
                            onClick={() => handleDelete(field)}>
                            <FiTrash2 size={16}/>
                          </button>
                        </div>
                      </td>
                    </tr>

                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {fields.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--gray-400)' }}>
          <FiDatabase size={36} style={{ marginBottom: 12, opacity: 0.4 }}/>
          <p>Sem campos definidos. Execute a migração SQL primeiro.</p>
        </div>
      )}

      {/* ── Field Modal ── */}
      {modalItem !== null && (
        <FieldModal
          initial={modalItem?.field_id ? modalItem : null}
          entityTypes={allTypes}
          onSave={async (payload) => {
            if (modalItem?.field_id) {
              await handleUpdate(modalItem.field_id, payload);
            } else {
              await handleCreate(payload);
            }
          }}
          onCancel={() => setModalItem(null)}
        />
      )}

      <div className="toast-container">
        {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast}/>)}
      </div>
    </div>
  );
};

export default NotificationEntityFieldsAdmin;